import { Injectable } from '@angular/core';
import { VCPM } from 'pcs-domain';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { of } from 'rxjs';
import { Internal } from '../../core/decorators/app.decorators';
import { AppConstants } from '../../core/data-models/app-constants';
import { DataProvider } from 'pcs-domain';
import { VersionUtils } from '../../core/utils/version-utils';

@Internal()
@Injectable({
  providedIn: 'root'
})
export class WorkloadDomainSizingService {
  workloadProfileForm: FormGroup = this.fb.group({});

  constructor(private fb: FormBuilder) { }

  /**
   * Determines if storageEfficiencyPreference control should be included
   * @param workloadInputConfig Configuration from API
   * @param vcfVersion VCF version from project metadata
   * @returns true if the control should be included
   */
  private shouldIncludeStorageEfficiencyPreference(workloadInputConfig: DataProvider.DefaultInputConfig | undefined, vcfVersion?: string): boolean {
    const vsanArch = workloadInputConfig?.storageConfiguration.vsanArchitecture.default.value;
    const profileMode = workloadInputConfig?.storageConfiguration.vsanProfileMode?.default.value;
    
    if (!vsanArch) return false;
    
    // Existing condition: vSAN OSA + AF Profile
    if (vsanArch.toLowerCase() === AppConstants.vSAN_OSA.toLowerCase() && 
        profileMode === AppConstants.PROFILE_MODE[0].code) {
      return true;
    }
    
    // New condition: vSAN ESA + VCF version >= 9.1.0 
    // (ESA is always AF, so no profile mode check needed)
    if (vsanArch.toLowerCase() === AppConstants.vSAN_ESA.toLowerCase() && 
        vcfVersion && VersionUtils.isVsanEsaDedupSupported(vcfVersion)) {
      return true;
    }
    
    return false;
  }


  createWorkloadProfileForm(savedWorkloadProfile: VCPM.Workload, workloadInputConfig: DataProvider.DefaultInputConfig | undefined, workloadId: string | null, vcfVersion?: string) {
    const workloadProfileFormConfig = this.generateWorkloadTypeSpecificConfigForm(workloadInputConfig, workloadInputConfig?.workload.workloadType.default.value, workloadInputConfig?.clusterConfiguration.storageEfficiencyPreference?.default.value);
      this.workloadProfileForm = this.fb.group({
        id: workloadId,
        storageConfiguration: this.generateStorageConfigurationForm(workloadInputConfig),
        vcfDeploymentInputs: this.fb.group({
          deploymentType: ['HCI', [Validators.required]]
        }),
        hardwareConfiguration: this.generateHardwareConfigurationForm(workloadInputConfig, vcfVersion),
        clusterConfiguration: this.generateClusterConfigurationForm(workloadInputConfig, vcfVersion),
        // Single reference control shared by the HCI side (points at a type:'HCI' pool entry) and
        // the dHCI-Compute side (points at a type:'HCI' or type:'COMPUTE' pool entry) - which kind
        // it is comes from the referenced entity's own `type`, never from a separate control.
        computeCluster: this.generateClusterReferenceForm(),
        computeClusterHardwareConfiguration: this.generateHardwareConfigurationForm(workloadInputConfig, vcfVersion, 'COMPUTE'),
        computeClusterConfiguration: this.generateClusterConfigurationForm(workloadInputConfig, vcfVersion, 'COMPUTE'),
        storageClusterHardwareConfiguration: this.generateHardwareConfigurationForm(workloadInputConfig, vcfVersion, 'STORAGE'),
        storageClusterConfiguration: this.generateClusterConfigurationForm(workloadInputConfig, vcfVersion, 'STORAGE'),
        storageCluster: this.generateClusterReferenceForm(),
        workload: this.getWorkloadProfileForm(workloadInputConfig),
        workloadDetails: workloadProfileFormConfig.workloadDetails,
        advancedSettings: this.fb.group({
          resourceUtilizationPlan: workloadProfileFormConfig.resourceUtilizationPlan,
          vSANOverheads: workloadProfileFormConfig.vSANOverheads,
        })
      });
      this.syncActiveClusterFormGroups(this.workloadProfileForm);
      return this.workloadProfileForm;
    }

  getWorkloadProfileForm(workloadInputConfig: DataProvider.DefaultInputConfig | undefined) {
    let workload: FormGroup;
    workload = this.fb.group({
      workload: [workloadInputConfig?.workload.workload.default.value, [Validators.required]],
      workloadType: [workloadInputConfig?.workload.workloadType.default.value, [Validators.required]],
      workloadName: ["General Purpose", [Validators.required]],
      workloadProfileType: []
    });

    return workload;
  }

  generateWorkloadTypeSpecificConfigForm(workloadInputConfig: DataProvider.DefaultInputConfig | undefined, workloadType: string = 'GVM', storageEffPre: string | undefined = undefined) {
    let workloadDetailsProperties: (keyof DataProvider.WorkloadDetails)[];
    let workloadAdvanceSettingsRup: (keyof DataProvider.ResourceUtilizationPlan)[];
    let workloadAdvanceSettingsOverheads: (keyof DataProvider.VSANOverheads)[];
    let workloadDetails: FormGroup;
    let resourceUtilizationPlan: FormGroup = this.fb.group({});
    let vSANOverheads: FormGroup = this.fb.group({});

    workloadDetails = this.fb.group({
      hasDataImported: ['manual'],
    });
    //TODO: driveType changes read_intesive need to take care only for ESA, not supported in OSA. 'averageWorkloadHours'
    //TODO: aggregateIops

    workloadDetailsProperties = ["totalCountOfVms", "storagePerVM", "vcpuPerCore", "vcpuPerVM", "vmemPerVM", "iopsPerVM", "hostFailuresToTolerate", "faultToleranceMethod", "ioAccessPattern", "ioSize", "ioRatio", "compression"];
    workloadAdvanceSettingsRup = ["avgMemoryConsumption", "avgConsumption"];//averageCPUUtilization averageMemoryUtilization
    workloadAdvanceSettingsOverheads = ["coresConsumed", "capacityWriteIOAmplifier", "maxIOpsPerDisk", "cacheWriteIOAmplifier", "maxBandwidthPerDisk"];
    switch (workloadType) {
      case "GVM":
      case "ORA":
      case "SQL":
      case "FCL":

        if(workloadDetailsProperties.includes('sysDisposableDiskSize') || workloadDetailsProperties.includes('numOfInstClonePools')) {
          workloadDetailsProperties = workloadDetailsProperties.splice(workloadDetailsProperties.findIndex(x => x === 'sizeOfMasterVM'), 1);
          workloadDetailsProperties = workloadDetailsProperties.splice(workloadDetailsProperties.findIndex(x => x === 'numOfDisksInMasterVM'), 1);
          workloadDetailsProperties = workloadDetailsProperties.splice(workloadDetailsProperties.findIndex(x => x === 'numOfLinkedClonePools'), 1);
          workloadDetailsProperties = workloadDetailsProperties.splice(workloadDetailsProperties.findIndex(x => x === 'expecVmdkGrowth'), 1);
          workloadDetailsProperties = workloadDetailsProperties.splice(workloadDetailsProperties.findIndex(x => x === 'persistentDiskSize'), 1);
          workloadDetailsProperties = workloadDetailsProperties.splice(workloadDetailsProperties.findIndex(x => x === 'sysDisposableDiskSize'), 1);
          workloadDetailsProperties = workloadDetailsProperties.splice(workloadDetailsProperties.findIndex(x => x === 'expecCloneGrowth'), 1);
          workloadDetailsProperties = workloadDetailsProperties.splice(workloadDetailsProperties.findIndex(x => x === 'numOfInstClonePools'), 1);
        }
        break;
        
      case "LCL":
        workloadDetailsProperties = workloadDetailsProperties.concat(["sizeOfMasterVM", "numOfDisksInMasterVM", "numOfLinkedClonePools", "expecVmdkGrowth", "persistentDiskSize", "sysDisposableDiskSize"]); //does not need for storagePerVM, while requires all other propertiers of GPW including RPU, Overheads
        const indexOfStoragePerVM = workloadDetailsProperties.indexOf('storagePerVM');
        if (indexOfStoragePerVM !== -1) {
          workloadDetailsProperties.splice(indexOfStoragePerVM, 1);
        }
        break;
        
      case "ICL":
        workloadDetailsProperties = workloadDetailsProperties.concat(["sizeOfMasterVM", "expecCloneGrowth", "numOfInstClonePools"]); //does not need for storagePerVM, while requires all other propertiers of GPW including RPU, Overheads
        const index = workloadDetailsProperties.indexOf('storagePerVM');
        if (index !== -1) {
          workloadDetailsProperties.splice(index, 1);
        }
        break;

      default:

        break;
    }

    //add or remove cacheWriteIOAmplifier from workloadAdvanceSettingsOverheads based on isOSA
    if (workloadInputConfig?.advancedSettings.vSANOverheads.cacheWriteIOAmplifier) {
      if (!workloadAdvanceSettingsOverheads.includes('cacheWriteIOAmplifier')) {
        workloadAdvanceSettingsOverheads.push('cacheWriteIOAmplifier');
      }
    } else if (workloadAdvanceSettingsOverheads.includes('cacheWriteIOAmplifier')) {
      const index = workloadAdvanceSettingsOverheads.indexOf('cacheWriteIOAmplifier');
      if (index !== -1) {
        workloadAdvanceSettingsOverheads.splice(index, 1);
      }
    }
    if(storageEffPre) {
      if((workloadInputConfig?.clusterConfiguration?.storageEfficiencyPreference?.default.value === 'dedupeAndCompression' || storageEffPre === 'dedupeAndCompression') && !workloadDetailsProperties.includes('dedup')) {
        workloadDetailsProperties.push('dedup');
        const index = workloadDetailsProperties.indexOf('compression');
        if (index !== -1) {
          workloadDetailsProperties.splice(index, 1);
        }
      } else if((workloadInputConfig?.clusterConfiguration?.storageEfficiencyPreference?.default.value === 'compressionOnly' || storageEffPre === 'compressionOnly')) {
        workloadDetailsProperties.push('compression');
        const index = workloadDetailsProperties.indexOf('dedup');
        if (index !== -1) {
          workloadDetailsProperties.splice(index, 1);
        }
    } else {
      //none case
      const compIndex = workloadDetailsProperties.indexOf('compression');
      if (compIndex !== -1) {
        workloadDetailsProperties.splice(compIndex, 1);
      }
      const dedupIndex = workloadDetailsProperties.indexOf('dedup');
      if (dedupIndex !== -1) {
        workloadDetailsProperties.splice(dedupIndex, 1);
      }
    }
    } else {
      if(!workloadInputConfig?.workloadDetails.compression) {
        const compIndex = workloadDetailsProperties.indexOf('compression');
        if (compIndex !== -1) {
          workloadDetailsProperties.splice(compIndex, 1);
        }
      }
      const dedupIndex = workloadDetailsProperties.indexOf('dedup');
      if (dedupIndex !== -1) {
        workloadDetailsProperties.splice(dedupIndex, 1);
      }
    }
    if(workloadInputConfig?.workloadDetails.autoMode && !workloadDetailsProperties.includes('autoMode')) {
      workloadDetailsProperties.push('autoMode');
    } else {
      const index = workloadDetailsProperties.indexOf('autoMode');
      if (index !== -1) {
        workloadDetailsProperties.splice(index, 1);
      }
    }

    workloadDetailsProperties.forEach((prop, index) => {
      
      let propertyData: any = workloadInputConfig?.workloadDetails?.[prop];
      if(propertyData) {
        if(propertyData?.value || propertyData?.max) {
          this.createFormControl(workloadDetails, prop, propertyData.value, propertyData?.max, propertyData?.min, true);
        } else if(propertyData?.default) {
          this.createFormControl(workloadDetails, prop, propertyData.default.value, 0, 0, false);
        }
      } else { //mostly for total count of vms
        this.createFormControl(workloadDetails, prop, null, 0, 0, false);
      }
    });

    // dataReductionRatio is a UI-level field derived from dedup/compression; seed its default
    // and validators from whichever legacy field the current SEP maps to, since the BE-facing
    // compression/dedup controls are never rendered and shouldn't be relied on for validation.
    let dataReductionDefault: number | null = null;
    let dataReductionValidators: ValidatorFn[] = [];
    if (storageEffPre === 'compressionOnly') {
      const compressionConfig = workloadInputConfig?.workloadDetails?.compression;
      const v = compressionConfig?.value;
      dataReductionDefault = v != null ? Number(v) : null;
      dataReductionValidators = [Validators.required, Validators.min(compressionConfig?.min ?? 0), Validators.max(compressionConfig?.max ?? 100), Validators.pattern(/^\d+(\.\d+)?$/)];
    } else if (storageEffPre === 'dedupeAndCompression') {
      const dedupConfig = workloadInputConfig?.workloadDetails?.dedup;
      const v = dedupConfig?.value;
      dataReductionDefault = v != null ? Number(v) : null;
      dataReductionValidators = [Validators.required, Validators.min(dedupConfig?.min ?? 0), Validators.max(dedupConfig?.max ?? 100), Validators.pattern(/^\d+(\.\d+)?$/)];
    }
    workloadDetails.addControl('dataReductionRatio', this.fb.control(dataReductionDefault, dataReductionValidators));

    workloadAdvanceSettingsRup.forEach((prop, index) => {
      let propertyData: any = workloadInputConfig?.advancedSettings?.resourceUtilizationPlan?.[prop];
      if(propertyData && propertyData?.value) {
        this.createFormControl(resourceUtilizationPlan, prop, propertyData.value, propertyData?.max, propertyData?.min, true);
      } else {
        this.createFormControl(resourceUtilizationPlan, prop, null, 0, 0, false);
      }
    });

    workloadAdvanceSettingsOverheads.forEach((prop, index) => {
      let propertyData: any = workloadInputConfig?.advancedSettings?.vSANOverheads?.[prop];
      if(propertyData && propertyData?.value) {
        this.createFormControl(vSANOverheads, prop, propertyData.value, 0, 0, false, true);
      }
    });

    return {
      workloadDetails,
      resourceUtilizationPlan,
      vSANOverheads
    };
  }

  generateHardwareConfigurationForm(inputConfig: DataProvider.DefaultInputConfig | undefined, vcfVersion?: string, clusterRole?: 'COMPUTE' | 'STORAGE', liveVsanArchitecture?: string) {
    let hardwareConfiguration: FormGroup = this.fb.group({});
    // dHCI role groups take their network speed limits/defaults from the clusterType=D_HCI config
    // (HCI/no-role groups are untouched); a missing overlay or id keeps the HCI-derived values.
    const dhci = clusterRole ? inputConfig?.dhci : undefined;
    const workloadInputConfig = dhci?.networkSpeed && inputConfig
      ? { ...inputConfig, hardwareConfiguration: { ...inputConfig.hardwareConfiguration, networkSpeed: dhci.networkSpeed } }
      : inputConfig;

    // Get vSAN architecture once for the entire method. Prefer the caller's live selection
    // (workloadProfileForm.get('storageConfiguration.vsanArchitecture')?.value) over
    // workloadInputConfig's schema default - on a rebuild triggered by the user changing
    // architecture, workloadInputConfig can still reflect the pre-change config for a brief
    // window (async round-trip via getConfigBasedOnVsanArchSelected()), which previously caused
    // the wrong OSA/ESA control set (e.g. missing networkSpeed/networkSpeedNS) to be built.
    const vsanArchitecture = liveVsanArchitecture || workloadInputConfig?.storageConfiguration.vsanArchitecture.default.value;
    const isOSA = vsanArchitecture && vsanArchitecture.toLocaleLowerCase() === (AppConstants.vSAN_OSA).toLocaleLowerCase();

    // Add hardwareConfigMode control
    // For OSA (or VCF 5.2.1, where ReadyNode isn't available on either architecture): default to
    // 'cpu-based' - No Preference means "let the sizing engine pick a ReadyNode for you", so it
    // requires ReadyNode-based sizing itself to be eligible, exactly like readynode-based does.
    // Otherwise default to 'no-preference' (workload-first sizing).
    const defaultMode = VersionUtils.isReadyNodeEligible(vsanArchitecture, vcfVersion) ? 'no-preference' : 'cpu-based';
    
    hardwareConfiguration.addControl('hardwareConfigMode', this.fb.control(defaultMode, [Validators.required]));
    
    // Add CPU-based config form group
    // If defaulting to ReadyNode mode, create CPU controls as disabled
    const cpuControlsDisabled = defaultMode !== 'cpu-based';
    hardwareConfiguration.addControl('cpuBasedConfig', this.fb.group({
      cpuVendor: this.fb.control({value: null, disabled: cpuControlsDisabled}, [Validators.required]),
      cpuGeneration: this.fb.control({value: null, disabled: cpuControlsDisabled}, [Validators.required]),
      cpuSeries: this.fb.control({value: null, disabled: cpuControlsDisabled}, [Validators.required]),
      cpuCategory: this.fb.control({value: null, disabled: cpuControlsDisabled}, [Validators.required]),
      cpuModel: this.fb.control({value: null, disabled: cpuControlsDisabled}, [Validators.required]),
    }));
    
    // Add ReadyNode-based config form group
    hardwareConfiguration.addControl('readyNodeBasedConfig', this.fb.group({
      readyNodeSelection: this.fb.control(null),
      recommendedProfileName: this.fb.control(null),
      searchFilters: this.fb.group({
        vendorId: [''],
        profile: [''],
        serverModel: [''],
        generationId: [''],
        freeText: ['']
      })
    }));

    // Add ESXi-based config form group - Compute cluster hardware only, mirrors the ESXi radio's
    // own role === 'COMPUTE' gate in ClusterHardwareConfigComponent. Controls start disabled
    // (defaultMode is never 'esxi') - ClusterHardwareConfigComponent.onModeChange('esxi') enables
    // them. Bounds/validators for esxiCommonConfig aren't schema-driven like commonConfig below -
    // they come from the selected server's own detail response, so EsxiServerSelectorComponent
    // applies Validators.min/max dynamically per selection instead.
    if (clusterRole === 'COMPUTE') {
      hardwareConfiguration.addControl('esxiBasedConfig', this.fb.group({
        esxiServerSelection: this.fb.control(null),
        searchFilters: this.fb.group({
          vendorId: [''],
          cpuModelSeries: [''],
          generationId: [''],
          freeText: ['']
        })
      }));
      hardwareConfiguration.addControl('esxiCommonConfig', this.fb.group({
        memory: this.fb.control({ value: null, disabled: true }),
        coresPerSocket: this.fb.control({ value: null, disabled: true }),
        clockSpeed: this.fb.control({ value: null, disabled: true }),
        sockets: this.fb.control({ value: null, disabled: true }),
      }));
    }

    // Add common config form group (used by both CPU and ReadyNode modes)
    // Use config-driven approach to get default values and validators
    const commonConfigGroup = this.fb.group({});
    
    // Define properties that belong to commonConfig
    const commonConfigProperties: (keyof DataProvider.HardwareConfiguration)[] = [
      'sockets', 'cores', 'clockSpeed', 'numberOfDrives', 
      'driveSize', 'customDriveSize', 'serverRam'
    ];
    
    // Add architecture-specific properties
    if (isOSA) {
      // Add OSA-specific properties
      const commonConfigPropertiesOSA: (keyof DataProvider.HardwareConfiguration)[] = ['cacheTierMediaRating', 'diskGroup', 'userDefinedDiskGroups'];
      commonConfigProperties.push(...commonConfigPropertiesOSA);
    } else {
      // Add ESA-specific properties (networkSpeed only for ESA)
      const commonConfigPropertiesESA: (keyof DataProvider.HardwareConfiguration)[] = ['networkSpeed'];
      commonConfigProperties.push(...commonConfigPropertiesESA);
    }
    
    // Iterate and create controls from config (same pattern as old flat structure)
    commonConfigProperties.forEach((prop) => {
      const propertyData: any = workloadInputConfig?.hardwareConfiguration?.[prop];
      
      if (propertyData) {
        // Check if it's a ValueUnit structure (has value, min, max directly)
        if (propertyData?.value !== undefined && propertyData?.value !== null) {
          // ValueUnit structure: {value, min, max, unit}
          // Used by: cores, clockSpeed, numberOfDrives, serverRam, networkSpeed
          const hasValidators = (propertyData.max !== undefined || propertyData.min !== undefined);
          this.createFormControl(
            commonConfigGroup,
            prop,
            propertyData.value,
            propertyData.max || 0,
            propertyData.min || 0,
            hasValidators
          );
        } 
        // Check if it's a DefaultOptions2 structure (has default and options)
        else if (propertyData?.default) {
          // DefaultOptions2 structure: {default: {value, min, max, unit}, options: [...]}
          // Used by: sockets, driveSize
          const defaultValue = propertyData.default?.value !== undefined ? propertyData.default.value : null;
          let maxValue = propertyData.default?.max !== undefined ? propertyData.default.max : 0;
          let minValue = propertyData.default?.min !== undefined ? propertyData.default.min : 0;
          let hasValidators = (propertyData.default?.max !== undefined || propertyData.default?.min !== undefined);
          
          // Special handling for fields that don't have max/min in default but need validation from options
          if ((prop === 'sockets' || prop === 'driveSize') && !hasValidators && propertyData.options) {
            const numericValues = propertyData.options
              .map((option: any) => Number(option.value))
              .filter((value: number) => !isNaN(value) && value !== 0);
            if (numericValues.length > 0) {
              maxValue = Math.max(...numericValues);
              minValue = Math.min(...numericValues);
              hasValidators = true;
            }
          }
          if (prop === 'driveSize' && hasValidators) {
            // driveSize is a <select> that can also hold the non-numeric "other"/"Other" sentinel
            // (customDriveSize captures the actual number in that case, via onDriveSizeChange()) -
            // createFormControl's full-validator branch would add a numeric-only pattern validator
            // that "other"/"Other" always fails, incorrectly flagging the control as invalid the
            // moment that option is picked. Use required+min+max only, never that pattern.
            commonConfigGroup.addControl(prop, this.fb.control(defaultValue, [Validators.required, Validators.max(maxValue), Validators.min(minValue)]));
          } else {
            this.createFormControl(
              commonConfigGroup,
              prop,
              defaultValue,
              maxValue,
              minValue,
              hasValidators
            );
          }
        }
        else {
          // Fallback for properties without proper config data structure
          this.createFormControl(commonConfigGroup, prop, null, 0, 0, false);
        }
      } else {
        // Special case: customDriveSize has no config (optional field for "other" option)
        if (prop === 'customDriveSize') {
          commonConfigGroup.addControl(prop, this.fb.control(null));
        } else {
          this.createFormControl(commonConfigGroup, prop, null, 0, 0, false);
        }
      }
    });

    // vSAN Max (dHCI) Storage role only, ESA: second Network Speed control for the E/W-N/S split
    // (see ClusterHardwareConfigComponent.showSplitNetworkSpeed()). Forwarded to the sizing-engine
    // request as CPUAvailable.networkSpeedNS (see buildChassisFromHardwareConfig() in
    // sizing-request.adapter.ts) - mirrors networkSpeed's own control (same starting
    // value/validators) since the API schema for the two fields is otherwise identical.
    if (!isOSA && clusterRole === 'STORAGE') {
      const networkSpeedControl = commonConfigGroup.get('networkSpeed');
      const nsLimits = dhci?.networkSpeedNS;
      if (nsLimits?.value !== undefined && nsLimits?.value !== null) {
        this.createFormControl(commonConfigGroup, 'networkSpeedNS', nsLimits.value, nsLimits.max || 0, nsLimits.min || 0, nsLimits.max !== undefined || nsLimits.min !== undefined);
      } else {
        commonConfigGroup.addControl('networkSpeedNS', this.fb.control(
          networkSpeedControl?.value ?? null,
          networkSpeedControl?.validator ? [networkSpeedControl.validator] : []
        ));
      }
    }

    hardwareConfiguration.addControl('commonConfig', commonConfigGroup);

    return hardwareConfiguration;
  }

  /**
   * `clusterRole` omitted -> today's exact HCI behavior (full field set), unchanged.
   * 'COMPUTE'/'STORAGE' -> the narrower vSAN Max (dHCI) field subsets from the confirmed field
   * split. `networkTrafficSeparation` has no config-driven default anywhere (new field, no
   * backend contract yet) - stubbed to `false`.
   */
  generateClusterConfigurationForm(inputConfig: DataProvider.DefaultInputConfig | undefined, vcfVersion?: string, clusterRole?: 'COMPUTE' | 'STORAGE') {
    let clusterConfiguration: FormGroup = this.fb.group({});
    // dHCI role groups have their own growth factor limits/defaults (computeGrowthFactor /
    // storageGrowthFactor); HCI/no-role keeps `growthFactor`. Missing id keeps the HCI-derived one.
    const roleGrowthFactor = clusterRole === 'COMPUTE' ? inputConfig?.dhci?.computeGrowthFactor
      : clusterRole === 'STORAGE' ? inputConfig?.dhci?.storageGrowthFactor : undefined;
    const workloadInputConfig = roleGrowthFactor && inputConfig
      ? { ...inputConfig, clusterConfiguration: { ...inputConfig.clusterConfiguration, growthFactor: roleGrowthFactor } }
      : inputConfig;
    let clusterConfigurationProperties: (keyof DataProvider.ClusterConfiguration)[];
    if (clusterRole === 'COMPUTE') {
      clusterConfigurationProperties = ['clusterType', 'accountForSwapSpace', 'growthFactor', 'cpuHeadroomSteadyState', 'cpuHeadroomHostFailureScenario'];
    } else if (clusterRole === 'STORAGE') {
      clusterConfigurationProperties = ['clusterType', 'operationsReserve', 'hostRebuildReserve', 'growthFactor'];
    } else {
      clusterConfigurationProperties = ['clusterType', 'operationsReserve', 'hostRebuildReserve', 'accountForSwapSpace', 'growthFactor', 'cpuHeadroomSteadyState', 'cpuHeadroomHostFailureScenario'];
    }
    let storageEfficiencyProperties: (keyof DataProvider.ClusterConfiguration)[] = ['storageEfficiencyPreference'];

    // Storage Efficiency Preference lives only on the top-level (HCI, clusterRole omitted)
    // clusterConfiguration control, rendered once under "Storage Configuration" - it's never added
    // to either dHCI role's own clusterConfiguration, removing the duplicate that used to render
    // under Storage Cluster Configuration (cleanup task).
    if (clusterRole === undefined && this.shouldIncludeStorageEfficiencyPreference(workloadInputConfig, vcfVersion)) {
      clusterConfigurationProperties = clusterConfigurationProperties.concat(storageEfficiencyProperties);
    }
    clusterConfigurationProperties.forEach((prop, index) => {
      let propertyData: any = workloadInputConfig?.clusterConfiguration?.[prop];
      if (propertyData && propertyData?.value) {
        this.createFormControl(
          clusterConfiguration,
          prop,
          propertyData.value,
          propertyData?.max,
          propertyData?.min,
          true
        );
      } else {
        this.createFormControl(clusterConfiguration, prop, propertyData?.default.value, 0, 0, false);
      }
    });

    if (clusterRole === 'STORAGE') {
      // Storage swap was previously disabled in requests; missing saved values remain off.
      clusterConfiguration.addControl('accountForSwapSpace', this.fb.control(false));
      clusterConfiguration.addControl('networkTrafficSeparation', this.fb.control(false));
    }
    return clusterConfiguration;
  }

  /** {id, name} reference to a shared, reusable cluster entity - always disabled, never user-editable. */
  generateClusterReferenceForm(): FormGroup {
    return this.fb.group({
      id: this.fb.control<string | null>({ value: null, disabled: true }),
      name: this.fb.control<string | null>({ value: null, disabled: true }),
    });
  }

  /**
   * Enables/disables the HCI vs. dHCI Compute/Storage cluster groups to match the current
   * deploymentType, keeping the inactive side's required fields out of the form's `.valid` walk
   * (Risk #1). Purely structural - never clears values. Call at form creation and after every
   * deploymentType change (pair with clearInactiveClusterSelectionOnToggle for an actual change).
   */
  syncActiveClusterFormGroups(workloadProfileForm: FormGroup): void {
    const isDhci = workloadProfileForm.get('vcfDeploymentInputs.deploymentType')?.value === 'dHCI';
    const hciGroups = ['hardwareConfiguration', 'clusterConfiguration'];
    hciGroups.forEach((name) => {
      const group = workloadProfileForm.get(name);
      if (isDhci) {
        group?.disable();
      } else {
        group?.enable();
      }
    });
    // enable()/disable() always cascade to every descendant control - Angular's `onlySelf` option
    // only suppresses propagation to ancestors, never to children. Blanket-enabling a Compute/
    // Storage pair's *hardware* group while it's pinned to 'use-existing-cluster' would stomp the
    // field-level disabled state ClusterHardwareConfigComponent already derived from the referenced
    // entity's real mode (getDisplayMode()) - re-enabling cpuBasedConfig/esxiCommonConfig fields the
    // picked cluster's mode doesn't use, leaving them enabled+required+null and permanently
    // invalidating the form on every re-sync (this runs synchronously inside onEdit(), right after
    // the form is correctly patched with saved data). Only the hardware group's re-enable is skipped
    // for that reason; disabling when a side goes inactive is left unconditional since that side is
    // leaving the form entirely.
    const isComputeUseExisting = (workloadProfileForm.get('computeClusterHardwareConfiguration') as FormGroup | null)
      ?.get('hardwareConfigMode')?.value === 'use-existing-cluster';
    const isStorageUseExisting = (workloadProfileForm.get('storageClusterHardwareConfiguration') as FormGroup | null)
      ?.get('hardwareConfigMode')?.value === 'use-existing-cluster';
    const dhciPairs: Array<[string, string, boolean]> = [
      ['computeClusterHardwareConfiguration', 'computeClusterConfiguration', isComputeUseExisting],
      ['storageClusterHardwareConfiguration', 'storageClusterConfiguration', isStorageUseExisting],
    ];
    dhciPairs.forEach(([hwName, cfgName, isUseExisting]) => {
      // Unlike hwName, the *cluster-configuration* group (clusterType, headroom, reserves, etc.) has
      // no hardwareConfigMode-dependent field-level state, so it's always safe to enable while this
      // pair is the active (dHCI) side - even while pinned to 'use-existing-cluster'. Skipping it the
      // same way hwName must was leaving a referencer's cluster-configuration controls stuck disabled
      // from an earlier pass (e.g. this shared form having gone through an HCI-mode or inactive-side
      // edit before returning to this dHCI referencer) - Fix C now lets a referencer's
      // cluster-configuration edits persist, so the UI must let them be made.
      const cfgGroup = workloadProfileForm.get(cfgName);
      if (isDhci) {
        cfgGroup?.enable();
      } else {
        cfgGroup?.disable();
      }
      // STORAGE-only exception: Operations Reserve/Host Rebuild Reserve must stay disabled on a
      // stretched cluster (setReserveControlsBasedOnClusterType() in ClusterHardwareConfigComponent).
      // That method won't re-run here since this enable() doesn't replace the group (no ngOnChanges
      // fires), so re-apply the rule immediately after the blanket enable() above cascades to it.
      if (isDhci && cfgName === 'storageClusterConfiguration' && cfgGroup?.get('clusterType')?.value === 'stretched') {
        cfgGroup.get('operationsReserve')?.setValue(false);
        cfgGroup.get('operationsReserve')?.disable();
        cfgGroup.get('hostRebuildReserve')?.setValue(false);
        cfgGroup.get('hostRebuildReserve')?.disable();
      }

      if (isDhci && isUseExisting) {
        return;
      }
      const hwGroup = workloadProfileForm.get(hwName);
      if (isDhci) {
        hwGroup?.enable();
      } else {
        hwGroup?.disable();
      }
    });
    // Storage Efficiency Preference is independent of deploymentType (HCI vs. dHCI) - it always
    // lives on the shared clusterConfiguration group and must stay usable regardless of which side
    // is currently active, so re-enable it even though the group it lives on may have just been
    // disabled above.
    workloadProfileForm.get('clusterConfiguration.storageEfficiencyPreference')?.enable({ emitEvent: false });
  }

  /**
   * Clear-on-toggle (confirmed decision - no restore-on-switch-back): on every deploymentType
   * transition, nulls the vacated side's reference(s) and resets its mirror fields to plain
   * standard defaults (re-running the same generator functions a brand-new workload gets) -
   * never cross-mapped/borrowed from the other side. Call only on an actual transition, not at
   * initial form creation (createWorkloadProfileForm already builds fresh groups there).
   */
  clearInactiveClusterSelectionOnToggle(
    workloadProfileForm: FormGroup,
    newDeploymentType: 'HCI' | 'dHCI',
    workloadInputConfig: DataProvider.DefaultInputConfig | undefined,
    vcfVersion?: string,
  ): void {
    const liveVsanArchitecture = workloadProfileForm.get('storageConfiguration.vsanArchitecture')?.value;
    // computeCluster is shared by both sides - always cleared on toggle regardless of direction,
    // same "no restore-on-switch-back" rule as before the HCI/dHCI-Compute reference collapse.
    workloadProfileForm.get('computeCluster')?.patchValue({ id: null, name: null });
    if (newDeploymentType === 'dHCI') {
      workloadProfileForm.setControl('hardwareConfiguration', this.generateHardwareConfigurationForm(workloadInputConfig, vcfVersion, undefined, liveVsanArchitecture));
      workloadProfileForm.setControl('clusterConfiguration', this.generateClusterConfigurationForm(workloadInputConfig, vcfVersion));
    } else {
      workloadProfileForm.get('storageCluster')?.patchValue({ id: null, name: null });
      workloadProfileForm.setControl('computeClusterHardwareConfiguration', this.generateHardwareConfigurationForm(workloadInputConfig, vcfVersion, 'COMPUTE', liveVsanArchitecture));
      workloadProfileForm.setControl('computeClusterConfiguration', this.generateClusterConfigurationForm(workloadInputConfig, vcfVersion, 'COMPUTE'));
      workloadProfileForm.setControl('storageClusterHardwareConfiguration', this.generateHardwareConfigurationForm(workloadInputConfig, vcfVersion, 'STORAGE', liveVsanArchitecture));
      workloadProfileForm.setControl('storageClusterConfiguration', this.generateClusterConfigurationForm(workloadInputConfig, vcfVersion, 'STORAGE'));
    }
    this.syncActiveClusterFormGroups(workloadProfileForm);
  }

  generateStorageConfigurationForm(workloadInputConfig: DataProvider.DefaultInputConfig | undefined) {
    let storageConfiguration: FormGroup = this.fb.group({});
    let storageConfigurationProperties: (keyof DataProvider.StorageConfiguration)[] = ['storageType', 'vsanArchitecture'];
    let storageConfigurationPropertiesOSA: (keyof DataProvider.StorageConfiguration)[] = ['vsanProfileMode'];
    if(workloadInputConfig?.storageConfiguration.vsanArchitecture.default.value && (workloadInputConfig?.storageConfiguration.vsanArchitecture.default.value).toLocaleLowerCase() === (AppConstants.vSAN_OSA).toLocaleLowerCase()) {
      storageConfigurationProperties = storageConfigurationProperties.concat(storageConfigurationPropertiesOSA);
    }
    storageConfigurationProperties.forEach((prop, index) => {
      let propertyData = workloadInputConfig?.storageConfiguration?.[prop];
      const optionsPropertyData = propertyData as DataProvider.DefaultOptions;
      if(propertyData && optionsPropertyData?.default) {
        this.createFormControl(storageConfiguration, prop, optionsPropertyData?.default.value, 0, 0, false);
      }
        this.createFormControl(storageConfiguration, prop, null, 0, 0, false);
    });
    // storageConfiguration.addControl('dataEncryption', this.fb.control(false));
    return storageConfiguration;
  }

  adjustFormBasedOnvSANArchSelected(workloadProfileForm: FormGroup, workloadInputConfig: DataProvider.DefaultInputConfig | undefined, savedStorageEffPref?: string, vcfVersion?: string) {
    const currentDeploymentType = workloadProfileForm.get('vcfDeploymentInputs.deploymentType')?.value;
    const liveVsanArchitecture = workloadProfileForm.get('storageConfiguration.vsanArchitecture')?.value;
    workloadProfileForm.setControl('storageConfiguration', this.generateStorageConfigurationForm(workloadInputConfig));
    workloadProfileForm.get('storageConfiguration')?.updateValueAndValidity();
    workloadProfileForm.setControl('clusterConfiguration', this.generateClusterConfigurationForm(workloadInputConfig, vcfVersion));
    workloadProfileForm.get('clusterConfiguration')?.updateValueAndValidity();
    workloadProfileForm.setControl('hardwareConfiguration', this.generateHardwareConfigurationForm(workloadInputConfig, vcfVersion, undefined, liveVsanArchitecture));
    workloadProfileForm.get('hardwareConfiguration')?.updateValueAndValidity();
    // vSAN Max (dHCI): the Compute/Storage hardware+cluster groups depend on workloadInputConfig/
    // vcfVersion exactly like HCI's own groups do, so an architecture change must rebuild them too -
    // never the *ClusterReference groups (Risk #5), which this deliberately leaves untouched. Nor a
    // group currently sitting on the 'use-existing-cluster' sentinel: this method re-runs on every
    // workloadInputConfig re-supply (vSAN architecture/profile-mode/storage-type changes), which can
    // happen while the user is mid-edit of a picked cluster's still-enabled fields (e.g. sockets) -
    // unconditionally setControl()-ing here would silently discard that edit before "Update Workload"
    // ever reads it, since a fresh setControl() has no memory of what was just typed.
    const isComputeUseExisting = (workloadProfileForm.get('computeClusterHardwareConfiguration') as FormGroup | null)?.get('hardwareConfigMode')?.value === 'use-existing-cluster';
    if (!isComputeUseExisting) {
      workloadProfileForm.setControl('computeClusterHardwareConfiguration', this.generateHardwareConfigurationForm(workloadInputConfig, vcfVersion, 'COMPUTE', liveVsanArchitecture));
      workloadProfileForm.get('computeClusterHardwareConfiguration')?.updateValueAndValidity();
      workloadProfileForm.setControl('computeClusterConfiguration', this.generateClusterConfigurationForm(workloadInputConfig, vcfVersion, 'COMPUTE'));
      workloadProfileForm.get('computeClusterConfiguration')?.updateValueAndValidity();
    }
    const isStorageUseExisting = (workloadProfileForm.get('storageClusterHardwareConfiguration') as FormGroup | null)?.get('hardwareConfigMode')?.value === 'use-existing-cluster';
    if (!isStorageUseExisting) {
      workloadProfileForm.setControl('storageClusterHardwareConfiguration', this.generateHardwareConfigurationForm(workloadInputConfig, vcfVersion, 'STORAGE', liveVsanArchitecture));
      workloadProfileForm.get('storageClusterHardwareConfiguration')?.updateValueAndValidity();
      workloadProfileForm.setControl('storageClusterConfiguration', this.generateClusterConfigurationForm(workloadInputConfig, vcfVersion, 'STORAGE'));
      workloadProfileForm.get('storageClusterConfiguration')?.updateValueAndValidity();
    }
    this.syncActiveClusterFormGroups(workloadProfileForm);
    // Use saved storageEfficiencyPreference when editing, otherwise use default from config
    const storageEffPref = savedStorageEffPref || workloadInputConfig?.clusterConfiguration.storageEfficiencyPreference?.default.value;
    const workloadProfileFormConfig = this.generateWorkloadTypeSpecificConfigForm(workloadInputConfig, workloadInputConfig?.workload.workloadType.default.value, storageEffPref);
    workloadProfileForm.setControl('workload', this.getWorkloadProfileForm(workloadInputConfig));
    workloadProfileForm.setControl('workloadDetails', workloadProfileFormConfig.workloadDetails);
    (workloadProfileForm.get('advancedSettings') as FormGroup).setControl('vSANOverheads', workloadProfileFormConfig.vSANOverheads);
    (workloadProfileForm.get('advancedSettings') as FormGroup).setControl('resourceUtilizationPlan', workloadProfileFormConfig.resourceUtilizationPlan);
    workloadProfileForm.setControl('vcfDeploymentInputs', this.fb.group({
      deploymentType: [currentDeploymentType || 'HCI', [Validators.required]]
    }));
  }


  createFormControl(formGroup: FormGroup, controlName: string, ctrlValue: number | boolean | string | null, ctrlMax: number = 0, ctrlMin: number = 0, validationRequired: boolean = true, onlyPattern: boolean = false) {
    let ctrl: AbstractControl;
    if(validationRequired) {
      ctrl = this.fb.control(ctrlValue, [Validators.required, Validators.max(ctrlMax), Validators.min(ctrlMin), Validators.pattern(/^\d+(\.\d+)?$/)]);
    } else if(onlyPattern) {
      ctrl = this.fb.control(ctrlValue, [Validators.required, Validators.pattern(/^\d+(\.\d+)?$/)]);
    }else {
      ctrl = this.fb.control(ctrlValue, [Validators.required]);
    }
    formGroup.addControl(controlName, ctrl);
  }

 generateThreeDigitIdFromList(workloadList: VCPM.Workload[] | undefined): string | null {
  const existingIds = new Set(workloadList?.map(workload => workload.id));

  for (let i = 0; i < 1000; i++) {
    const id = i.toString().padStart(3, '0');
    if (!existingIds.has(id)) {
      return id;
    }
  }

  // All IDs are used
  return null;
}

generateUniqueTwoDigitNumberId(existingNumbers: (number | undefined)[]): number | undefined {
  const max = 99;

  for (let i = 1; i <= max; i++) {
    if (existingNumbers && !existingNumbers.includes(i)) {
      return i; // return the first (smallest) unused number
    }
  }

  return undefined; // all numbers used
}


  triggerFormValidation(formGroup: FormGroup, path: string = '') {
  Object.keys(formGroup.controls).forEach(key => {
    const control = formGroup.get(key);
    const currentPath = path ? `${path}.${key}` : key;

    if (control instanceof FormControl) {
      control.markAsTouched();
      control.updateValueAndValidity({ onlySelf: true });
      
      // Log invalid controls with their errors
      if (control.invalid) {
        console.log(`❌ Invalid control: ${currentPath}`, {
          value: control.value,
          errors: control.errors,
          validators: control.validator
        });
      }
    }

    if (control instanceof FormGroup) {
      this.triggerFormValidation(control, currentPath);  // recursive for nested groups
    }

    if (control instanceof FormArray) {
      control.controls.forEach((ctrl, index) => {
        if (ctrl instanceof FormGroup || ctrl instanceof FormControl) {
          this.triggerFormValidation(ctrl as FormGroup, `${currentPath}[${index}]`);
        }
      });
    }
  });

  // Final global update
  formGroup.updateValueAndValidity();
  
  // Log summary at root level
  if (!path) {
    console.log('=== Form Validation Summary ===');
    console.log('Form valid:', formGroup.valid);
    console.log('Form errors:', formGroup.errors);
  }
}

}

